import { useEffect, useState } from 'react';
import { useI18n } from '../i18n/I18nProvider';
import { storage } from '../storage';
import { Button } from './primitives';

export type Theme = 'light' | 'dark';

const THEME_KEY = 'daghfal.theme';
const DARK_QUERY = '(prefers-color-scheme: dark)';

/** The theme this device chose, or `null` to follow the system — which is where everyone starts. */
export function readStoredTheme(): Theme | null {
  const stored = storage.read(THEME_KEY);
  return stored === 'light' || stored === 'dark' ? stored : null;
}

/**
 * Stamps the choice on the root element. Dark mode is a token swap keyed on `data-theme` (tokens.css), so
 * this attribute is all it takes; with none, the `prefers-color-scheme` query decides.
 */
export function applyTheme(theme: Theme | null): void {
  const root = globalThis.document?.documentElement;
  if (!root) return;
  if (theme) root.dataset.theme = theme;
  else delete root.dataset.theme;
}

function systemTheme(): Theme {
  return globalThis.matchMedia?.(DARK_QUERY).matches ? 'dark' : 'light';
}

/**
 * Switches between light and dark.
 *
 * Until it is pressed the page follows the system; the first press pins the opposite of whatever is showing,
 * and the choice is kept per device, like the language.
 */
export function ThemeToggle() {
  const { t } = useI18n();
  const [stored, setStored] = useState<Theme | null>(readStoredTheme);
  const [system, setSystem] = useState<Theme>(systemTheme);

  // While nothing is pinned the system can change under us (a phone switching at dusk), and the pressed
  // state has to follow it.
  useEffect(() => {
    const query = globalThis.matchMedia?.(DARK_QUERY);
    if (!query) return;
    const onChange = () => setSystem(systemTheme());
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  const current = stored ?? system;

  const toggle = () => {
    const next: Theme = current === 'dark' ? 'light' : 'dark';
    setStored(next);
    applyTheme(next);
    storage.write(THEME_KEY, next);
  };

  return (
    <Button
      variant="ghost"
      className="dg-theme-toggle"
      aria-pressed={current === 'dark'}
      aria-label={t('pref.theme.dark')}
      title={t('pref.theme')}
      onClick={toggle}
    >
      <span aria-hidden="true">{current === 'dark' ? '☀️' : '🌙'}</span>
    </Button>
  );
}
